import { useState } from 'react'
import {
  ChevronDown,
  LogOut,
  Mic,
  MicOff,
  NotebookPen,
  PhoneOff,
  ScreenShare,
  ScreenShareOff,
  SwitchCamera,
  Users,
  Video,
  VideoOff,
} from 'lucide-react'
import { useMediaDeviceSelect, useRoomContext, useTrackToggle } from '@livekit/components-react'
import { facingModeFromLocalTrack, LocalVideoTrack, Track } from 'livekit-client'
import { Button } from '@/custolink-newdesign/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/custolink-newdesign/ui/dropdown-menu'
import { cn } from '@/custolink-newdesign/lib/utils'

type BarLayout = 'desktop' | 'mobile'

function controlClass(layout: BarLayout, extra?: string) {
  return cn(
    'cl:flex cl:flex-col cl:items-center cl:justify-center cl:gap-0.5 cl:rounded-md cl:text-white cl:hover:bg-white/10 cl:disabled:opacity-50 cl:disabled:pointer-events-none',
    layout === 'desktop'
      ? 'cl:h-10 cl:flex-row cl:gap-1.5 cl:px-3 cl:text-sm'
      : 'cl:min-h-[44px] cl:min-w-[56px] cl:px-2 cl:py-1 cl:text-[11px] cl:text-center cl:phone-landscape:w-full',
    extra,
  )
}

function labelTextClass(layout: BarLayout) {
  return layout === 'desktop' ? undefined : 'cl:leading-none'
}

/**
 * Device picker for one media-device kind, built on useMediaDeviceSelect following the existing
 * CameraSwitchButton pattern (VideoRoomPage.tsx) - hidden whenever there is nothing to pick
 * between, including audiooutput on Safari/iOS, which never exposes more than one device.
 */
function DeviceMenu(p: { kind: MediaDeviceKind; label: string }) {
  const room = useRoomContext()
  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({
    kind: p.kind,
    room,
  })

  if (devices.length <= 1) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`${p.label} options`}
          className="cl:min-h-[44px] cl:min-w-[28px] cl:text-white/80 cl:hover:bg-white/10 cl:hover:text-white"
        >
          <ChevronDown className="cl:size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center">
        <DropdownMenuLabel>{p.label}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={activeDeviceId}
          onValueChange={(id) => {
            void setActiveMediaDevice(id)
          }}
        >
          {devices.map((d, i) => (
            <DropdownMenuRadioItem key={d.deviceId} value={d.deviceId}>
              {d.label || `Device ${i + 1}`}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function MicButton(p: { layout: BarLayout }) {
  const { enabled, buttonProps } = useTrackToggle({ source: Track.Source.Microphone })
  const Icon = enabled ? Mic : MicOff
  const label = enabled ? 'Mute' : 'Unmute'
  return (
    <button type="button" {...buttonProps} aria-label={label} className={controlClass(p.layout)}>
      <Icon className="cl:size-5" />
      <span className={labelTextClass(p.layout)}>{label}</span>
    </button>
  )
}

function CameraButton(p: { layout: BarLayout }) {
  const { enabled, buttonProps } = useTrackToggle({ source: Track.Source.Camera })
  const Icon = enabled ? Video : VideoOff
  const label = enabled ? 'Stop video' : 'Start video'
  return (
    <button type="button" {...buttonProps} aria-label={label} className={controlClass(p.layout)}>
      <Icon className="cl:size-5" />
      <span className={labelTextClass(p.layout)}>{label}</span>
    </button>
  )
}

function ScreenShareButton(p: { layout: BarLayout }) {
  const { enabled, buttonProps } = useTrackToggle({ source: Track.Source.ScreenShare })
  const Icon = enabled ? ScreenShareOff : ScreenShare
  const label = enabled ? 'Stop sharing' : 'Share screen'
  return (
    <button type="button" {...buttonProps} aria-label={label} className={controlClass(p.layout)}>
      <Icon className="cl:size-5" />
      <span className={labelTextClass(p.layout)}>{label}</span>
    </button>
  )
}

/**
 * Cycles the local camera between rear and front (D-05). Tries the standards-based
 * facingMode restart first; desktop webcams report no facing mode at all, so a change that
 * leaves the reported facing mode unchanged falls back to cycling devices, the same approach
 * CameraSwitchButton already uses elsewhere.
 */
export function FlipCameraButton() {
  const room = useRoomContext()
  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({
    kind: 'videoinput',
    room,
  })
  const [flipping, setFlipping] = useState(false)

  if (devices.length <= 1) return null

  const handleFlip = async () => {
    setFlipping(true)
    try {
      const publication = room.localParticipant.getTrackPublication(Track.Source.Camera)
      const videoTrack = publication?.videoTrack as LocalVideoTrack | undefined
      if (videoTrack == null) return

      const before = facingModeFromLocalTrack(videoTrack).facingMode
      const next = before === 'environment' ? 'user' : 'environment'
      await videoTrack.restartTrack({ facingMode: next })

      const after = facingModeFromLocalTrack(videoTrack).facingMode
      if (after === before) {
        const currentIndex = devices.findIndex((d) => d.deviceId === activeDeviceId)
        const nextIndex = (currentIndex + 1) % devices.length
        await setActiveMediaDevice(devices[nextIndex].deviceId)
      }
    } catch {
      console.warn('Flip camera failed')
    } finally {
      setFlipping(false)
    }
  }

  return (
    <button
      type="button"
      disabled={flipping}
      aria-label="Flip camera"
      className={controlClass('mobile')}
      onClick={() => {
        void handleFlip()
      }}
    >
      <SwitchCamera className="cl:size-5" />
      <span className="cl:leading-none">Flip camera</span>
    </button>
  )
}

/**
 * The room's bottom control bar (D-04), desktop and mobile variants. A plain custom bar, not
 * LiveKit's own prefab bar - every control needs a visible text label, which the prefab does
 * not offer, and Leave must stay a disconnect-only action per D-01 (the caller passes
 * room.disconnect as onLeave, never a close-session mutation).
 */
export function CallBar(p: {
  layout: BarLayout
  onLeave: () => void
  canEndSession?: boolean
  onEndSession?: () => void
  endSessionPending?: boolean
  sidePanelLabel?: string
  sidePanelOpen?: boolean
  onToggleSidePanel?: () => void
  thumbnailsCollapsed?: boolean
  onToggleThumbnails?: () => void
}) {
  const canShareScreen =
    typeof navigator !== 'undefined' && navigator.mediaDevices?.getDisplayMedia != null

  return (
    <div
      className={cn(
        'rs-call-bar cl:flex cl:items-center cl:justify-center cl:gap-1.5 cl:bg-neutral-950/95 cl:text-white cl:border-t cl:border-white/10 cl:px-2 cl:py-1.5',
        p.layout === 'mobile' && 'cl:pb-[env(safe-area-inset-bottom)]',
        // Sideways on a phone the bar lives in the room's narrow right-hand column: stack it.
        p.layout === 'mobile' && 'cl:phone-landscape:flex-col cl:phone-landscape:gap-1 cl:phone-landscape:border-t-0 cl:phone-landscape:py-2',
      )}
    >
      <MicButton layout={p.layout} />
      {p.layout === 'desktop' && <DeviceMenu kind="audioinput" label="Microphone" />}

      <CameraButton layout={p.layout} />
      {p.layout === 'desktop' && <DeviceMenu kind="videoinput" label="Camera" />}

      {p.layout === 'desktop' && <DeviceMenu kind="audiooutput" label="Speaker" />}

      {p.layout === 'desktop' && canShareScreen && <ScreenShareButton layout={p.layout} />}

      {p.layout === 'mobile' && <FlipCameraButton />}

      {p.onToggleThumbnails != null && (
        <button
          type="button"
          aria-pressed={!p.thumbnailsCollapsed}
          className={controlClass(p.layout)}
          onClick={p.onToggleThumbnails}
        >
          <Users className="cl:size-5" />
          <span className={labelTextClass(p.layout)}>
            {p.thumbnailsCollapsed ? 'Show participants' : 'Hide participants'}
          </span>
        </button>
      )}

      {p.onToggleSidePanel != null && (
        <button
          type="button"
          aria-pressed={p.sidePanelOpen}
          className={controlClass(p.layout)}
          onClick={p.onToggleSidePanel}
        >
          <NotebookPen className="cl:size-5" />
          <span className={labelTextClass(p.layout)}>{p.sidePanelLabel ?? 'Notes'}</span>
        </button>
      )}

      <button type="button" className={controlClass(p.layout)} onClick={p.onLeave}>
        <LogOut className="cl:size-5" />
        <span className={labelTextClass(p.layout)}>Leave</span>
      </button>

      {p.canEndSession === true && p.onEndSession != null && (
        <button
          type="button"
          disabled={p.endSessionPending}
          title="End this session for everyone"
          className={cn(
            controlClass(p.layout),
            'cl:text-red-400 cl:hover:bg-red-500/10',
            p.layout === 'desktop' && 'cl:ml-auto',
          )}
          onClick={p.onEndSession}
        >
          <PhoneOff className="cl:size-5" />
          <span className={labelTextClass(p.layout)}>End session</span>
        </button>
      )}
    </div>
  )
}
